import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CompetencyAPIService } from '@app/domain/competency/api/competency-api.service';
import { CompetencyRatingConfiguration } from '@app/domain/competency/model/competency-rating-configuration.model';
import { CompetencyRating } from '@app/domain/competency/model/competency-rating.model';
import { Competency } from '@app/domain/competency/model/competency.model';
import { CompetencyBusinessService } from '@app/domain/competency/service/competency-business.service';
import { TerminologyEntity } from '@app/domain/terminology/model/terminology-entity.enum';
import { CompanyFeatures } from '@app/models/company-features.model';
import { IState } from '@app/models/state/state.model';
import { Globals } from '@app/shared/globals/globals';
import { PaginationNewComponent } from '@app/shared/pagination/pagination-new/pagination-new.component';
import { forkJoin, firstValueFrom } from 'rxjs';

interface PageCompetency extends Competency {
  competencyRating: CompetencyRating;
}

type TabEntity = PageCompetency;

@Component({
  selector: 'app-information-sidebar-competencies',
  templateUrl: './information-sidebar-competencies.component.html',
  styleUrls: ['./information-sidebar-competencies.component.scss'],
})
export class InformationSidebarCompetenciesComponent implements OnInit {
  public readonly eTerminologyEntity = TerminologyEntity;
  public readonly eCompanyFeatures = CompanyFeatures;

  @ViewChild('pagination') pagination?: PaginationNewComponent;

  @Input() userIds: number[];
  @Input() userIdsManaging: number[];

  state: IState;
  data: {
    master: TabEntity[];
    filtered: TabEntity[];
    display: TabEntity[];
  };

  competencyRatings: CompetencyRating[];
  competencyRatingConfiguration: CompetencyRatingConfiguration;
  isManagingSingleUser: boolean;

  constructor(
    public globals: Globals,
    private competencyBusinessService: CompetencyBusinessService,
    private competencyAPIService: CompetencyAPIService
  ) {
    this.userIds = [];
    this.competencyRatings = [];
    this.userIdsManaging = [];

    this.isManagingSingleUser = false;

    this.state = {
      loading: true,
      error: false,
      errorMessage: '',
    };

    this.data = {
      master: [],
      filtered: [],
      display: [],
    };
  }

  ngOnInit(): void {
    this.getData();
  }

  getData(): void {
    if (!this.userIds) {
      return;
    }
    if (this.userIds.length === 0) {
      return;
    }

    if (this.userIds.length > 1) {
      this.getAllCompetenciesData();
      return;
    }

    this.isManagingSingleUser = this.userIdsManaging.includes(this.userIds[0]);
    if (this.globals.hasFeature(CompanyFeatures.SKILL_RATING)) {
      this.getCompetencyRatingData(this.userIds[0]).then(() =>
        this.getCompetenciesDataForUser(this.userIds[0])
      );
    } else {
      this.getCompetenciesDataForUser(this.userIds[0]);
    }
  }

  getCompetencyRatingData(userId: number): Promise<void> {
    return firstValueFrom(
      forkJoin([
        this.competencyAPIService.getRatingsForUser(userId),
        this.competencyAPIService.getRatingConfiguration(),
      ])
    ).then(([competencyRatings, competencyRatingConfiguration]) => {
      this.competencyRatings = competencyRatings;
      this.competencyRatingConfiguration = competencyRatingConfiguration;
    });
  }

  getCompetenciesDataForUser(userId: number): Promise<void> {
    return this.competencyBusinessService
      .getForUserId(userId)
      .then((competencies) => {
        let dataParsed = this.parseCompetencyToPageCompetencyMultiple(
          competencies,
          this.competencyRatings
        );
        dataParsed = this.limitRatingsToScale(
          dataParsed,
          this.competencyRatingConfiguration
        );
        dataParsed = this.sortData(dataParsed);

        this.populateData(dataParsed);

        this.state.loading = false;
      });
  }

  getAllCompetenciesData(): Promise<void> {
    return firstValueFrom(
      forkJoin([
        this.competencyBusinessService.getCompetencies(null, null, null),
        this.competencyAPIService.getRatingConfiguration(),
      ])
    ).then(([competencies, competencyRatingConfiguration]) => {
      this.competencyRatingConfiguration = competencyRatingConfiguration;

      let dataParsed = this.parseCompetencyToPageCompetencyMultiple(
        competencies,
        this.competencyRatings
      );

      dataParsed = this.limitRatingsToScale(
        dataParsed,
        this.competencyRatingConfiguration
      );
      dataParsed = this.sortData(dataParsed);
      this.populateData(dataParsed);
      this.state.loading = false;
    });
  }

  sortData(data: TabEntity[]): TabEntity[] {
    return (
      data
        // Order by name
        .sort((a, b) => a.name.localeCompare(b.name))
        // Order by score
        .sort(this.sortByCompetencyRatingScore)
    );
  }

  limitRatingsToScale(
    items: PageCompetency[],
    competencyRatingConfiguration: CompetencyRatingConfiguration
  ): PageCompetency[] {
    return items.map((item) => {
      if (!item.competencyRating) return item;

      if (
        item.competencyRating.scoreValue >
        competencyRatingConfiguration.scoreCount - 1
      ) {
        item.competencyRating.scoreValue =
          competencyRatingConfiguration.scoreCount - 1;
      }

      return item;
    });
  }

  sortByCompetencyRatingScore(a: PageCompetency, b: PageCompetency): number {
    if (!a.competencyRating && !b.competencyRating) return 0;
    if (!a.competencyRating) return 1;
    if (!b.competencyRating) return -1;

    return b.competencyRating.scoreValue - a.competencyRating.scoreValue;
  }

  populateData(data?: TabEntity[]): void {
    if (!data) {
      data = this.data.master;
    }
    this.data.master = data;
    this.data.filtered = data;

    // this.universalFilterData.searchProps = ['name'];
    // this.universalFilterData.filterOptions = data.map(i => this.getFilterOptionsForBrowseRole(i));

    this.refreshPagination();
  }

  refreshPagination(): void {
    if (this.pagination) {
      this.pagination.update();
    }
  }

  parseCompetencyToPageCompetencyMultiple(
    data: Competency[],
    ratings: CompetencyRating[]
  ): PageCompetency[] {
    return data.map((competency) =>
      this.parseCompetencyToPageCompetency(competency, ratings)
    );
  }

  parseCompetencyToPageCompetency(
    competency: Competency,
    ratings: CompetencyRating[]
  ): PageCompetency {
    const output: PageCompetency = competency as PageCompetency;

    if (output) {
      output.competencyRating = ratings.find(
        (rating) => rating.targetEntityId === competency.id
      );
    }

    return output;
  }
}
